import React from "react";

import { useBotEmbed } from "./BotEmbedContext";
import BotWidget from "./BotWidget";
import { DEFAULT_BOT_ID } from "./constants";
import { extractBotId } from "./mockData";

const TestSurface = ({
  shortCodeInput,
  setShortCodeInput,
  setSelectedBotId,
}) => {
  const { config, isLoadingConfig, isUsingFallback, loadError } = useBotEmbed();

  return (
    <div className="bb-embed-page bb-embed-page--test">
      <div className="bb-embed-test-shell">
        <div className="bb-embed-test-copy">
          <span className="bb-embed-kicker">Bot testing route</span>
          <h1>
            Test the reusable embed shell with a mock shortcode-driven setup.
          </h1>
          <p>
            Paste a bot id or a full `?id=` URL, then this screen will request
            the embed configuration and restyle the widget from the API
            response.
          </p>
        </div>

        <div className="bb-embed-test-grid">
          <div className="bb-embed-test-controls">
            <div className="bb-embed-field">
              <label htmlFor="bot-embed-short-code">Shortcode or bot id</label>
              <div className="bb-embed-field-row">
                <input
                  id="bot-embed-short-code"
                  onChange={(event) => setShortCodeInput(event.target.value)}
                  placeholder="Paste a shortcode or id"
                  value={shortCodeInput}
                />
                <button
                  className="bb-embed-primary-btn"
                  onClick={() => {
                    const nextBotId =
                      extractBotId(shortCodeInput) || DEFAULT_BOT_ID;
                    setShortCodeInput(nextBotId);
                    setSelectedBotId(nextBotId);
                  }}
                  type="button"
                >
                  Apply
                </button>
                <button
                  className="bb-embed-secondary-btn"
                  onClick={() => {
                    setShortCodeInput(DEFAULT_BOT_ID);
                    setSelectedBotId(DEFAULT_BOT_ID);
                  }}
                  type="button"
                >
                  Reset
                </button>
              </div>
              <div className="bb-embed-field-hint">
                The embed request is sent to
                `REACT_APP_BOT_EMBED_API_URL` with `domain` and `shortcode`
                headers, matching the older live widget contract.
              </div>
            </div>

            <div className="bb-embed-field">
              <span className="bb-embed-label">Appearance palette</span>
              <div className="bb-embed-swatch-row">
                {[
                  ["Header", config.styles.header_bg],
                  ["Launcher", config.styles.btn_color],
                  ["Bot bubble", config.styles.bot_tb_color],
                  ["Sender", config.styles.sender_tb_color],
                  ["Footer", config.styles.footer_bg],
                ].map(([label, color]) => (
                  <div className="bb-embed-swatch" key={label}>
                    <i style={{ background: color }} />
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bb-embed-field">
              <span className="bb-embed-label">Behavior snapshot</span>
              <div className="bb-embed-note">
                Template: {config.template_name} | Position:{" "}
                {config.bot_possition} | Default open:{" "}
                {config.show_default ? " yes" : " no"} | Human takeover:
                {config.human_takeover_attempt ? " enabled" : " disabled"}
              </div>
            </div>

            <div className="bb-embed-field">
              <span className="bb-embed-label">Request status</span>
              <div
                className={`bb-embed-inline-note bb-embed-status ${
                  isLoadingConfig
                    ? "bb-embed-status--loading"
                    : isUsingFallback
                      ? "bb-embed-status--fallback"
                      : "bb-embed-status--live"
                }`}
              >
                {isLoadingConfig
                  ? "Loading embed appearance from API..."
                  : isUsingFallback
                    ? loadError || "Using fallback embed settings."
                    : `Loaded live settings for ${config.bot_name}.`}
              </div>
            </div>
          </div>

          <div className="bb-embed-stage bb-embed-stage--test">
            <div className="bb-embed-stage-widget">
              <BotWidget />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestSurface;
